import { logInfo, logError, LogCategory, startMeasure, endMeasure } from '@/utils/logger';
import { supabase } from '@/integrations/supabase/client';

/**
 * Service for OpenAI thread operations
 * Centralizes all OpenAI thread-related API calls via Edge Functions
 */
export class OpenAIThreadsService {
  
  /**
   * Create a new OpenAI thread via Edge Function with credit checking
   * @param executeAIOperation - The wrapper function from useAIOperationWrapper
   * @returns Promise<string> - The OpenAI thread ID
   */
  static async createOpenAIThread(executeAIOperation?: (operation: () => Promise<any>, options?: any) => Promise<any>): Promise<string> {
    const startTime = startMeasure('createOpenAIThread');
    
    logInfo('Creating new OpenAI thread via Edge Function', { 
      category: LogCategory.API 
    });

    try {
      // If executeAIOperation is provided, use it for credit checking
      if (executeAIOperation) {
        const data = await executeAIOperation(
          async () => {
            const { data, error } = await supabase.functions.invoke('threaded-text-generation', {
              body: { type: 'create-thread' }
            });

            if (error) {
              throw new Error(`Edge Function error: ${error.message}`);
            }

            if (!data?.thread_id) {
              throw new Error('Edge Function succeeded but no thread ID returned');
            }

            return data;
          },
          {
            immediate: true,
            errorPrefix: 'Thread creation failed',
            debug: false
          }
        );

        logInfo('OpenAI thread created successfully via Edge Function with credit check', { 
          category: LogCategory.API,
          data: { openaiThreadId: data.thread_id }
        });

        endMeasure(startTime, 'OpenAI thread creation complete', LogCategory.API);
        return data.thread_id;
      } else {
        // Legacy direct call (will be deprecated)
        console.warn('[DEPRECATED] createOpenAIThread called without executeAIOperation. Credit checking bypassed.');
        
        const { data, error } = await supabase.functions.invoke('threaded-text-generation', {
          body: { type: 'create-thread' }
        });

        if (error) {
          throw new Error(`Edge Function error: ${error.message}`);
        }

        if (!data?.thread_id) {
          throw new Error('Edge Function succeeded but no thread ID returned');
        }

        logInfo('OpenAI thread created successfully via Edge Function (legacy)', { 
          category: LogCategory.API,
          data: { openaiThreadId: data.thread_id }
        });

        endMeasure(startTime, 'OpenAI thread creation complete', LogCategory.API);
        return data.thread_id;
      }

    } catch (error: any) {
      const errorMsg = `Failed to create OpenAI thread: ${error.message}`;
      logError('OpenAI thread creation failed', {
        category: LogCategory.API,
        data: { error: errorMsg }
      });
      endMeasure(startTime, 'OpenAI thread creation failed', LogCategory.API);
      throw new Error(errorMsg);
    }
  }

  /**
   * Delete an OpenAI thread via Edge Function
   * @param openaiThreadId - The OpenAI thread ID to delete
   */
  static async deleteOpenAIThread(openaiThreadId: string): Promise<void> {
    const startTime = startMeasure('deleteOpenAIThread');
    
    logInfo('Deleting OpenAI thread via Edge Function', { 
      category: LogCategory.API,
      data: { openaiThreadId }
    });

    try {
      const { error } = await supabase.functions.invoke('threaded-text-generation', {
        body: { 
          type: 'delete-thread',
          thread_id: openaiThreadId 
        }
      });

      if (error) {
        throw new Error(`Edge Function error: ${error.message}`);
      }

      logInfo('OpenAI thread deleted successfully via Edge Function', { 
        category: LogCategory.API,
        data: { openaiThreadId }
      });

      endMeasure(startTime, 'OpenAI thread deletion complete', LogCategory.API);

    } catch (error: any) {
      const errorMsg = `Failed to delete OpenAI thread: ${error.message}`;
      logError('OpenAI thread deletion failed', {
        category: LogCategory.API,
        data: { error: errorMsg, openaiThreadId }
      });
      endMeasure(startTime, 'OpenAI thread deletion failed', LogCategory.API);
      throw new Error(errorMsg);
    }
  }
} 